/**
 * Aba "Execuções" do editor de automações (issue #137).
 *
 * Lista as execuções mais recentes e, ao lado, o log por passo da selecionada:
 * status, resumo do que foi feito e a mensagem de erro quando falhou. Replay
 * e filtros avançados ficam para a fase 2.
 */

import { useCallback, useEffect, useState } from 'react'
import Icon from '../../components/icons.jsx'
import { autRun, autRuns } from '../../db/api.js'
import { metaFor } from './nodeMeta.js'

const REFRESH_MS = 15000

export const RUN_STATUS = {
  running: { label: 'Executando', badge: 'badge-blue' },
  waiting: { label: 'Aguardando', badge: 'badge-amber' },
  success: { label: 'Sucesso', badge: 'badge-green' },
  error: { label: 'Erro', badge: 'badge-red' },
  skipped: { label: 'Cancelada', badge: 'badge-neutral' },
}

const fmtDataHora = (iso) => (iso
  ? new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
  : '—')

function duracao(inicio, fim) {
  if (!inicio || !fim) return null
  const ms = new Date(fim) - new Date(inicio)
  if (ms < 1000) return `${ms} ms`
  if (ms < 60000) return `${(ms / 1000).toFixed(1)} s`
  return `${Math.round(ms / 60000)} min`
}

// Resumo legível do output de um passo (chaves conhecidas primeiro).
function resumoOutput(output) {
  if (!output) return null
  if (output.text) return `Mensagem: “${output.text}”`
  if (output.activity_id) return `Atividade: ${output.titulo}`
  if (output.deal_id && output.titulo) return `Negócio criado: ${output.titulo}`
  if (output.until) return `Retoma em ${fmtDataHora(output.until)}`
  if (output.rules?.length > 1) {
    const ok = output.rules.filter((r) => r.result).length
    return `${output.result ? 'Sim' : 'Não'} (${ok} de ${output.rules.length} regras)`
  }
  if (typeof output.result === 'boolean') {
    return `${output.result ? 'Sim' : 'Não'} (valor: ${output.actual || 'vazio'})`
  }
  if (output.resultado) return output.resultado
  if (output.inside) return 'Dentro do expediente — seguiu direto'
  if ('case' in output) return output.case ? `Caminho “${output.case}”` : 'Caminho “Outros”'
  if (output.aplicado_em) return `Responsável definido (${output.aplicado_em})`
  if (output.operacao) return `Tags — ${output.operacao}: ${output.tags}`
  if (output.status && output.url) return `HTTP ${output.status} · ${output.url}`
  if (output.unchanged) return 'Negócio já estava na etapa'
  if (output.stage_id) return 'Negócio movido de etapa'
  const pares = Object.entries(output).filter(([, v]) => v != null && typeof v !== 'object')
  return pares.slice(0, 3).map(([k, v]) => `${k}: ${v}`).join(' · ')
}

export default function AutomacaoExecucoes({ automationId, catalog }) {
  const [runs, setRuns] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedId, setSelectedId] = useState(null)
  const [detail, setDetail] = useState(null)

  const load = useCallback(async () => {
    try {
      const rows = await autRuns(automationId)
      setRuns(rows)
      setError(null)
      setSelectedId((cur) => cur || rows[0]?.id || null)
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }, [automationId])

  useEffect(() => {
    load()
    const t = setInterval(load, REFRESH_MS)
    return () => clearInterval(t)
  }, [load])

  useEffect(() => {
    if (!selectedId) { setDetail(null); return undefined }
    let vivo = true
    autRun(selectedId).then((r) => { if (vivo) setDetail(r) }).catch(() => { if (vivo) setDetail(null) })
    return () => { vivo = false }
  }, [selectedId, runs])

  if (loading) return <div className="aut-runs-empty">Carregando execuções…</div>
  if (error) return <div className="aut-runs-empty aut-error-text">{error}</div>
  if (!runs.length) {
    return (
      <div className="aut-runs-empty">
        <Icon n="timeline" s={28} c="var(--ter)" />
        <strong>Nenhuma execução ainda</strong>
        <span>Quando o gatilho acontecer com a automação ativa, cada execução aparece aqui com o passo a passo.</span>
      </div>
    )
  }

  return (
    <div className="aut-runs">
      <div className="aut-runs-list">
        <div className="aut-runs-list-head">
          <span className="t-label">Últimas {runs.length}</span>
          <button type="button" className="btn btn-ghost btn-xs" onClick={load}>
            <Icon n="refresh" s={13} /> Atualizar
          </button>
        </div>
        {runs.map((r) => {
          const st = RUN_STATUS[r.status] || RUN_STATUS.running
          return (
            <button
              type="button" key={r.id}
              className={`aut-run-row${r.id === selectedId ? ' is-active' : ''}`}
              onClick={() => setSelectedId(r.id)}
            >
              <span className={`badge ${st.badge}`}>{st.label}</span>
              {r.context?._test && <span className="badge badge-neutral">Teste</span>}
              <span className="aut-run-when">{fmtDataHora(r.started_at)}</span>
              <span className="aut-run-meta">{r.steps} passo(s)</span>
            </button>
          )
        })}
      </div>

      <div className="aut-run-detail">
        {!detail ? (
          <div className="aut-runs-empty">Selecione uma execução.</div>
        ) : (
          <>
            <header className="aut-run-detail-head">
              <div>
                <span className="t-label">Execução</span>
                <h3>{fmtDataHora(detail.started_at)}</h3>
              </div>
              <span className={`badge ${(RUN_STATUS[detail.status] || RUN_STATUS.running).badge}`}>
                {(RUN_STATUS[detail.status] || RUN_STATUS.running).label}
              </span>
            </header>
            {detail.error && (
              <div className="aut-alert aut-alert-error">
                <Icon n="alert" s={15} /> {detail.error}
              </div>
            )}
            {detail.status === 'waiting' && detail.resume_at && (
              <div className="aut-alert aut-alert-wait">
                <Icon n="clock" s={15} /> Continua em {fmtDataHora(detail.resume_at)}
              </div>
            )}
            <ol className="aut-steps">
              {detail.steps.map((s, i) => {
                const type = catalog[s.node_type]
                const meta = metaFor(type)
                const st = RUN_STATUS[s.status] || RUN_STATUS.success
                const dur = duracao(s.started_at, s.finished_at)
                return (
                  <li key={`${s.node_id}-${i}`} className={`aut-step is-${s.status}`}>
                    <span className={`aut-node-icon tone-${meta.tone}`}><Icon n={meta.icon} s={14} /></span>
                    <div className="aut-step-body">
                      <div className="aut-step-top">
                        <strong>{type?.label || s.node_type}</strong>
                        <span className={`badge ${st.badge}`}>{st.label}</span>
                        {dur && <span className="aut-step-dur">{dur}</span>}
                      </div>
                      {s.error
                        ? <p className="aut-error-text">{s.error}</p>
                        : resumoOutput(s.output) && <p>{resumoOutput(s.output)}</p>}
                      {s.output && Object.keys(s.output).length > 0 && (
                        <details className="aut-step-data">
                          <summary>Ver dados</summary>
                          <pre>{JSON.stringify(s.output, null, 2)}</pre>
                        </details>
                      )}
                    </div>
                  </li>
                )
              })}
            </ol>
          </>
        )}
      </div>
    </div>
  )
}
